import { useEffect, useRef, useState } from 'react'
import { Field } from 'formular'
import config from 'config'
import logger from 'logger'
import { track } from 'analytics'
import { openModal } from 'modals'
import { CancelledPromiseError, PaymentTokenError } from 'helpers'
import { usePaymentFlow } from 'modules/payment'

import useAmazonPay from './useAmazonPay'


const countryToClientIdMap: Record<string, string> = {
  US: config.recurlyAmazonPayClientId,
  CA: config.recurlyAmazonPayClientIdCA,
}
const sellerId = config.recurlyAmazonPaySellerId
const buttonId = 'amazon-button'


type UseAmazonPayButtonProps = {
  withAddressBook?: boolean
}

const useAmazonPayButton = (props?: UseAmazonPayButtonProps) => {
  const { withAddressBook = false } = props || {}

  const { submit, isProcessing, shippingForm } = usePaymentFlow()
  const { isFetching, isLoaded, error } = useAmazonPay()

  const submitRef = useRef<PaymentModule.PaymentFlowOnSubmit>(submit)
  submitRef.current = submit

  const [ country, setCountry ] = useState(shippingForm?.fields.country.state.value)
  const clientId = countryToClientIdMap[country] ?? countryToClientIdMap.US

  useEffect(() => {
    if (!shippingForm) {
      return
    }

    const updateCountry = (country: Field<string>['state']) => {
      setCountry(country.value)
    }

    const countryField = shippingForm.fields.country
    countryField.on('state change', updateCountry)

    return () => {
      countryField.off('state change', updateCountry)
    }
  }, [ shippingForm ])


  useEffect(() => {
    if (isLoaded && !error) {
      // TODO add initialization errors handling - added on 2021-09-02 by maddoger
      const handleWidget = (accessToken: string) => {

        const tokenGetter: PaymentModule.TokenGetter = () => new Promise<{ tokenId: string }>((resolve, reject) => {
          logger.info('[AmazonPay] open widget')

          openModal('amazonWidgetsModal', {
            accessToken,
            sellerId,
            withAddressBook,
            onClose: () => {
              logger.info('[AmazonPay] widget was closed')

              reject(new CancelledPromiseError())
            },
            onError: (error) => {
              logger.info('[AmazonPay] widget error', {
                extra: {
                  error,
                },
              })

              reject(new PaymentTokenError(error, error.message))
            },
            onSubmit: (token) => {
              logger.info('[AmazonPay] widget submitted', {
                extra: {
                  token,
                },
              })

              resolve({ tokenId: token.id })
            },
          })
        })

        submitRef.current({ tokenGetter, paymentMethod: 'AMAZON_PAY' })
      }

      const handleAuthorization = () => {
        // ATTN we cant start the flow here, because Amazon doesn't send any event whe user closes authorization popup
        //  and we can't stop the flow correctly
        logger.info('[AmazonPay] button click')

        track('Express checkout click', {
          paymentMethod: 'AMAZON_PAY',
        })

        const opts = {
          scope: 'profile payments:widget payments:shipping_address',
          popup: true,
        }

        // this handler invokes than user login with Amazon, or when he has already logged in
        window.amazon.Login.authorize(opts, (res) => {
          logger.info('[AmazonPay] handle authorize', {
            extra: {
              res,
            },
          })

          const { error, access_token: accessToken } = res

          if (error) {
            logger.info('[AmazonPay] authorization error', {
              extra: {
                code: error.getErrorCode(),
                message: error.getErrorMessage(),
              },
            })
          }
          else {
            handleWidget(accessToken)
          }
        })
      }

      const initButton = () => {
        window.amazon.Login.setClientId(clientId)

        new window.OffAmazonPayments.Button(buttonId, sellerId, {
          type: 'PwA',
          authorization: handleAuthorization,
          onError: (error) => {
            logger.error('[AmazonPay] button error', {
              extra: {
                error,
              },
            })
          },
        })
      }

      initButton()
    }
  }, [ isLoaded, error, withAddressBook, clientId ])

  return {
    buttonId,
    clientId,
    isFetching,
    isLoaded,
    isProcessing,
  }
}


export default useAmazonPayButton
